import React from 'react';
import { PokemonCard } from '../../types/pokemon';
import { Link, useLocation } from 'react-router-dom';
import { SUPABASE_IMAGE_BASE_URL } from '../../lib/constants';
import { useAuth } from '../../hooks/useAuth';
import { Plus, Minus } from 'lucide-react';
import { Checkbox } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useTranslation } from '../../hooks/useTranslation';

interface CardItemProps {
  card: PokemonCard;
  quantity?: number;
  onUpdateQuantity: (cardId: number, quantity: number) => void;
  readOnly?: boolean;
  onClick?: () => void;
  isSelected?: boolean;
  isBulkMode?: boolean;
  getPathForLang: (path: string, targetLang?: 'fr' | 'en') => string;
  setInfo?: {
    display_name: string;
    display_name_en?: string;
  };
}

export function CardItem({ 
  card, 
  quantity = 0, 
  onUpdateQuantity, 
  readOnly = false, 
  onClick,
  isSelected = false,
  isBulkMode = false,
  getPathForLang,
  setInfo
}: CardItemProps) {
  const { user } = useAuth();
  const location = useLocation();
  const { lang } = useTranslation();
  const cardName = lang === 'en' ? (card.card_en || card.card_fr) : card.card_fr;
  
  const imageUrl = card.image 
    ? `${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/' : ''}${card.image}`
    : null;

  const setCode = location.pathname.includes('/sets/') 
    ? location.pathname.split('/sets/')[1].split('/')[0]
    : null;

  const cardDetailPath = setCode 
    ? getPathForLang(`/sets/${setCode}/cards/${card.id}`)
    : getPathForLang(`/cards/${card.id}`);

  const setDisplayName = setInfo 
    ? (lang === 'en' ? setInfo.display_name_en || setInfo.display_name : setInfo.display_name)
    : card.set;

  const handleQuantityChange = (delta: number) => {
    if (onUpdateQuantity) {
      console.log('handleQuantityChange called with delta:', delta);
      console.log('current quantity:', quantity);
      onUpdateQuantity(card.id, quantity + delta);
    } else {
      console.warn('onUpdateQuantity is not defined');
    }
  };

  const handleQuantityInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuantity = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
    if (!isNaN(newQuantity) && onUpdateQuantity) {
      onUpdateQuantity(card.id, newQuantity);
    }
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    e.stopPropagation();
    if (onClick) {
      onClick();
    }
  };

  const cardContent = (
    <>
      {imageUrl && (
        <div className="aspect-[3/4] overflow-hidden py-4">
          <img
            src={imageUrl}
            alt={cardName || 'Carte Pokémon'}
            className={`w-full h-full object-contain transform group-hover:scale-105 transition-transform duration-300 ${
              quantity === 0 ? 'grayscale' : ''
            }`}
          />
        </div>
      )}
      <div className="p-4">
        <h3 className="text-lg font-semibold text-gray-900 truncate">
          {cardName}
        </h3>
        <div className="mt-1 flex items-center justify-between">
          <span className="text-sm text-gray-500 truncate">{setDisplayName}</span>
          <span className="px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full">
            {card.rarity}
          </span>
        </div>
      </div>
    </>
  );

  return (
    <div 
      className={`group relative bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden ${
        isSelected ? 'ring-2 ring-indigo-500' : ''
      }`}
    >
      {isBulkMode && (
        <div className="absolute top-2 left-2 z-10" onClick={e => e.stopPropagation()}>
          <Checkbox checked={isSelected} onChange={handleCheckboxChange} />
        </div>
      )}
      
      {isBulkMode ? (
        <div onClick={handleCardClick} className="block cursor-pointer">
          {cardContent}
        </div>
      ) : (
        <Link to={cardDetailPath} className="block cursor-pointer">
          {cardContent}
        </Link>
      )}
      
      {!isBulkMode && !readOnly && user && (
        <div className="absolute top-2 right-2 flex items-center gap-1 bg-white/90 backdrop-blur-sm rounded-lg p-1 shadow-lg">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleQuantityChange(-1);
            }}
            disabled={quantity === 0}
            className="p-1 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Minus className="w-4 h-4" />
          </button>
          <input
            type="number"
            value={quantity}
            onChange={handleQuantityInputChange}
            onClick={e => e.stopPropagation()}
            className="w-12 text-center border rounded-md text-center"
          />
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleQuantityChange(1);
            }}
            className="p-1 rounded-md hover:bg-gray-100"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}